import { useState, useCallback, useMemo } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import AddIcon from "@mui/icons-material/Add";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import { AddNvrDialog, type AddNvrFormValues } from "../../components/nvr/AddNvrDialog.tsx";
import React from "react";

export type NvrRecord = {
  id: string;
  name: string;
  ip: string;
  brand: string;
};

function buildDisplayIp(values: AddNvrFormValues): string {
  const host = values.host.trim();
  const port = values.port.trim();
  if (!host) return "—";
  const scheme = values.useHttps ? "https" : "http";
  const portSuffix = port && port !== "80" && port !== "443" ? `:${port}` : "";
  return `${scheme}://${host}${portSuffix}`;
}

/**
 * NVR list view: toolbar, table, empty state, Add NVR dialog.
 */
export function NvrManagementPage() {
  const [rows, setRows] = useState<NvrRecord[]>([]);
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [addOpen, setAddOpen] = useState(false);
  const [page, setPage] = useState(0);
  const rowsPerPage = 10;

  const pagedRows = useMemo(() => {
    const start = page * rowsPerPage;
    return rows.slice(start, start + rowsPerPage);
  }, [rows, page]);

  const pageCount = Math.max(1, Math.ceil(rows.length / rowsPerPage) || 1);

  const handleAddSubmit = useCallback(async (values: AddNvrFormValues) => {
    const id = `nvr-${Date.now()}`;
    const ip = buildDisplayIp(values);
    setRows((prev) => [...prev, { id, name: values.name.trim(), ip, brand: values.brand }]);
    setPage(0);
    console.info("[NvrManagementPage] NVR added", { id, name: values.name, brand: values.brand });
  }, []);

  const handleDeleteRow = useCallback((id: string) => {
    setRows((prev) => prev.filter((r) => r.id !== id));
    setSelected((s) => {
      const next = { ...s };
      delete next[id];
      return next;
    });
  }, []);

  const allSelected =
    pagedRows.length > 0 && pagedRows.every((r) => selected[r.id]);
  const someSelected = pagedRows.some((r) => selected[r.id]);

  const toggleAll = useCallback(() => {
    if (!pagedRows.length) return;
    const next = !allSelected;
    setSelected((prev) => {
      const copy = { ...prev };
      for (const r of pagedRows) {
        if (next) copy[r.id] = true;
        else delete copy[r.id];
      }
      return copy;
    });
  }, [allSelected, pagedRows]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        bgcolor: "#fff",
        p: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => setAddOpen(true)}
          sx={{
            textTransform: "none",
            fontFamily: "Roboto, sans-serif",
            fontWeight: 500,
            borderColor: "#D0D0D0",
            color: "#424242",
            bgcolor: "#FAFAFA",
            "&:hover": { borderColor: "#BDBDBD", bgcolor: "#F5F5F5" },
          }}
        >
          Add NVR
        </Button>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <IconButton
            size="small"
            aria-label="Previous page"
            disabled={page <= 0 || rows.length === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
          >
            <ChevronLeftIcon />
          </IconButton>
          <IconButton
            size="small"
            aria-label="Next page"
            disabled={page >= pageCount - 1 || rows.length === 0}
            onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
          >
            <ChevronRightIcon />
          </IconButton>
        </Box>
      </Box>

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          flex: 1,
          border: "1px solid #E5E7EB",
          borderRadius: 1,
          overflow: "auto",
        }}
      >
        <Table size="small" stickyHeader sx={{ tableLayout: "fixed" }}>
          <TableHead>
            <TableRow
              sx={{
                "& .MuiTableCell-head": {
                  bgcolor: "#F5F5F5",
                  fontFamily: "Roboto, sans-serif",
                  fontWeight: 600,
                  fontSize: 14,
                  color: "#212121",
                  borderBottom: "1px solid #E5E7EB",
                },
              }}
            >
              <TableCell padding="checkbox" sx={{ width: 48 }}>
                <Checkbox
                  indeterminate={someSelected && !allSelected}
                  checked={allSelected}
                  onChange={toggleAll}
                  inputProps={{ "aria-label": "select all on page" }}
                />
              </TableCell>
              <TableCell>
                <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                  NVR Name
                  <SwapVertIcon sx={{ fontSize: 16, color: "#757575" }} />
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                  NVR IP
                  <SwapVertIcon sx={{ fontSize: 16, color: "#757575" }} />
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                  NVR Brand
                  <SwapVertIcon sx={{ fontSize: 16, color: "#757575" }} />
                </Box>
              </TableCell>
              <TableCell align="right" sx={{ width: 56 }}>
                <Tooltip title="Delete">
                  <DeleteOutlineIcon sx={{ fontSize: 20, color: "#757575" }} />
                </Tooltip>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 8, borderBottom: "1px solid #E5E7EB" }}>
                  <Typography sx={{ fontFamily: "Roboto, sans-serif", fontSize: 14, color: "#6B7280" }}>
                    No matching records found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              pagedRows.map((row) => (
                <TableRow
                  key={row.id}
                  hover
                  sx={{
                    "& .MuiTableCell-body": {
                      fontFamily: "Roboto, sans-serif",
                      fontSize: 14,
                      borderBottom: "1px solid #E5E7EB",
                    },
                  }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={Boolean(selected[row.id])}
                      onChange={() =>
                        setSelected((s) => ({
                          ...s,
                          [row.id]: !s[row.id],
                        }))
                      }
                    />
                  </TableCell>
                  <TableCell>{row.name}</TableCell>
                  <TableCell sx={{ wordBreak: "break-all" }}>{row.ip}</TableCell>
                  <TableCell>{row.brand}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      aria-label={`Delete ${row.name}`}
                      onClick={() => handleDeleteRow(row.id)}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <AddNvrDialog open={addOpen} onClose={() => setAddOpen(false)} onSubmit={handleAddSubmit} />
    </Box>
  );
}

export default NvrManagementPage;
